<template>
  <div class="modal-overlay" @click.self="close">
    <div class="modal-container">
      <div class="modal-header">
        <h2>{{ isEdit ? '編輯諮詢記錄' : '新增諮詢記錄' }}</h2>
        <button class="close-btn" @click="close">&times;</button>
      </div>
      
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>學生姓名*</label>
            <input 
              v-model="form.student_name" 
              type="text" 
              required 
              placeholder="輸入學生姓名"
            >
          </div>
          
          <div class="form-group">
            <label>諮詢課程*</label>
            <select v-model="form.course_id" required>
              <option v-for="course in allCourses" :key="course.id" :value="course.id">
                {{ course.title }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label>學校*</label>
            <input 
              v-model="form.school" 
              type="text" 
              required 
              placeholder="輸入學校名稱"
            >
          </div>
          
          <div class="form-group">
            <label>年級*</label>
            <input 
              v-model="form.grade" 
              type="text" 
              required 
              placeholder="輸入年級"
            >
          </div>
          
          <div class="form-group">
            <label>家長姓名*</label>
            <input 
              v-model="form.parent_name" 
              type="text" 
              required 
              placeholder="輸入家長姓名"
            >
          </div>
          
          <div class="form-group">
            <label>聯繫方式*</label>
            <select v-model="form.contact_method" required>
              <option value="">請選擇</option>
              <option value="line">LINE</option>
              <option value="phone">電話</option>
            </select>
          </div>
          
          <div class="form-group" v-if="form.contact_method.toLowerCase() === 'line'">
            <label>LINE ID*</label>
            <input 
              v-model="form.line_id" 
              type="text" 
              :required="form.contact_method.toLowerCase() === 'line'"
              placeholder="輸入LINE ID"
            >
          </div>
          
          <div class="form-group" v-if="form.contact_method.toLowerCase() === 'phone'">
            <label>電話號碼*</label>
            <input 
              v-model="form.phone" 
              type="tel" 
              :required="form.contact_method.toLowerCase() === 'phone'"
              placeholder="輸入電話號碼"
            >
          </div>
          
          <div class="form-group">
            <label>方便聯繫時間*</label>
            <select v-model="form.contact_time" required>
              <option value="any">皆可</option>
              <option value="morning">早上</option>
              <option value="afternoon">下午</option>
              <option value="evening">晚上</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>推薦人</label>
            <input 
              v-model="form.referrer" 
              type="text" 
              placeholder="輸入推薦人"
            >
          </div>
          
          <div class="form-group">
            <label>狀態*</label>
            <select v-model="form.status" required>
              <option value="NEW">新諮詢</option>
              <option value="IN_PROGRESS">處理中</option>
              <option value="RESOLVED_SALE">已進班</option>
              <option value="RESOLVED_NON_SALE">未進班</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>備註</label>
            <textarea 
              v-model="form.notes" 
              rows="4" 
              placeholder="輸入備註信息"
            ></textarea>
          </div>
          
          <div class="form-actions">
            <button type="submit" class="button button-primary" :disabled="submitting">
              {{ submitting ? '處理中...' : '保存' }}
            </button>
            <button type="button" class="button button-outline" @click="close">
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { CREATE_COURSE_INQUIRY, UPDATE_COURSE_INQUIRY } from '@/graphql/queries'

const props = defineProps({
  inquiry: Object,
  isEdit: Boolean
})

const emit = defineEmits(['close', 'saved'])

const defualtForm = {
  student_name: '',
  course_id: 0,
  course_title: '',
  school: '',
  grade: '',
  parent_name: '',
  contact_method: 'line',
  contact_time: 'any',
  line_id: '',
  phone: '',
  referrer: '',
  status: 'NEW',
  notes: ''
}

const allCourses = [
  { id: 0, title: '數理王子課程諮詢' },
  { id: 1, title: '醫牙學測衝刺班' },
  { id: 2, title: '升私中數學衝刺班' },
  { id: 3, title: '曉明資優數學專班' },
  { id: 4, title: '衛道資優數學專班' },
  { id: 5, title: '學測數學菁英班' },
  { id: 6, title: 'APCS實作程式設計' },
  { id: 7, title: '全民英檢中高級密訓' },
  { id: 8, title: '劍橋英檢KET/PET' },
]

const form = ref({ ...defualtForm })

const submitting = ref(false)

// Initialize form with inquiry data if editing
watch(() => props.inquiry, (inquiry) => {
  if (inquiry) {
    form.value = { ...inquiry }
    delete form.value.id // Remove ID if present
    delete form.value.created_at // Remove timestamp if present
    form.value.course_id = typeof inquiry.course_id === 'string' ? parseInt(inquiry.course_id) : inquiry.course_id 
  } else {
    resetForm()
  }
}, { immediate: true })

const selectedCourseTitle = computed(() => {
  const course = allCourses.find(c => c.id === form.value.course_id)
  return course ? course.title : ''
})

const { mutate: createInquiry } = useMutation(CREATE_COURSE_INQUIRY)
const { mutate: updateInquiry } = useMutation(UPDATE_COURSE_INQUIRY)

function resetForm() {
  form.value = { ...defualtForm }
}

function close() {
  emit('close')
}

function handleSubmit() {
  submitting.value = true
  
  const input = { ...form.value }
  input.course_title = selectedCourseTitle.value // Ensure course title is set
  delete input.__typename // Remove GraphQL metadata if present
  
  if (props.isEdit) {
    updateInquiry({ 
      id: props.inquiry.id,
      input 
    }).then(() => {
      emit('saved')
    }).catch(err => {
      alert('更新失敗: ' + err.message)
    }).finally(() => {
      submitting.value = false
    })
  } else {
    createInquiry({ 
      input 
    }).then(() => {
      emit('saved')
    }).catch(err => {
      alert('創建失敗: ' + err.message)
    }).finally(() => {
      submitting.value = false
    })
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-medium);
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-medium);
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 1rem;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions button {
    width: 100%;
  }
}
</style>